import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, Clock, AlertTriangle, Shield, Eye, Edit2, Zap,
  Loader2, ChevronDown, ChevronUp, Users, AlertOctagon, Lock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Status badge configs
const CA_STATUS = {
  none: { label: 'Not Started', cls: 'bg-gray-100 text-gray-600' },
  commercial_pending: { label: 'Pending L1', cls: 'bg-amber-100 text-amber-700' },
  leader_approved: { label: 'L1 Approved', cls: 'bg-blue-100 text-blue-700' },
  bfr_approved: { label: 'L2 Approved', cls: 'bg-indigo-100 text-indigo-700' },
  verified: { label: 'Verified', cls: 'bg-emerald-100 text-emerald-700' },
};

const SLA_STATUS = {
  expected: { label: 'Expected', cls: 'bg-emerald-100 text-emerald-700' },
  urgent: { label: 'Urgent', cls: 'bg-amber-100 text-amber-700' },
  critical: { label: 'Critical', cls: 'bg-red-100 text-red-700' },
  completed: { label: 'Done', cls: 'bg-teal-100 text-teal-700' },
};

// --- Summary Card ---
const SummaryCard = ({ label, count, icon: Icon, color, active, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-xl p-4 border-2 transition-all hover:shadow-md ${onClick ? 'cursor-pointer' : ''} ${
      active ? `border-${color}-400 shadow-md ring-1 ring-${color}-200` : 'border-gray-100'
    }`}
    data-testid={`approval-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{count}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl bg-${color}-50 flex items-center justify-center`}>
        <Icon size={20} className={`text-${color}-500`} />
      </div>
    </div>
  </div>
);

// --- Actionable Lead Row ---
const LeadRow = ({ lead, onView, onApprove, showApprove, showEdit }) => {
  const [acting, setActing] = useState(false);
  const caInfo = CA_STATUS[lead.ca_status] || CA_STATUS.none;
  const slaInfo = SLA_STATUS[lead.checklist_status] || SLA_STATUS.expected;

  const handleApprove = async () => {
    setActing(true);
    await onApprove(lead.id);
    setActing(false);
  };

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`lead-row-${lead.id}`}>
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">{lead.hotel_name || 'Unnamed'}</p>
          <p className="text-xs text-gray-400">{lead.owner_name}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${caInfo.cls}`}>{caInfo.label}</span>
      </td>
      <td className="px-4 py-3">
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${slaInfo.cls}`}>{slaInfo.label}</span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">{lead.lead_owner || '-'}</td>
      <td className="px-4 py-3 text-xs text-gray-500">{lead.checklist_progress}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <button onClick={() => onView(lead.id)} className="p-1.5 rounded-lg text-gray-500 hover:bg-teal-50 hover:text-teal-600 transition-colors" title="View Lead" data-testid={`view-lead-${lead.id}`}>
            <Eye size={14} />
          </button>
          {showEdit && (
            <button onClick={() => onView(lead.id)} className="p-1.5 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition-colors" title="Edit Lead" data-testid={`edit-lead-${lead.id}`}>
              <Edit2 size={14} />
            </button>
          )}
          {showApprove && (
            <button
              onClick={handleApprove}
              disabled={acting}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
              title="Quick Approve"
              data-testid={`approve-lead-${lead.id}`}
            >
              {acting ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
              Approve
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// --- Collapsible Lead Table ---
const LeadTable = ({ leads, title, emptyMsg, onView, onApprove, showApprove, showEdit, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!leads || leads.length === 0) return null;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`lead-table-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
        <span className="text-sm font-semibold text-gray-700">{title} ({leads.length})</span>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Lead</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Approval</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">SLA</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Assigned</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Checklist</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <LeadRow key={l.id} lead={l} onView={onView} onApprove={onApprove} showApprove={showApprove} showEdit={showEdit} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ================== ROLE-SPECIFIC SECTIONS ==================

// GTM Leader Insights
export const GTMApprovalInsights = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('commercial');

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/approval-analytics`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setData(d.gtm);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (leadId) => {
    try {
      await fetch(`${API_BASE}/api/leads/${leadId}/commercial/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-teal-500" /></div>;
  if (!data) return null;

  const cl = data.commercial_l1;
  const li = data.lead_info;
  const sla = data.checklist_sla;

  return (
    <div className="space-y-5" data-testid="gtm-approval-insights">
      <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
        <Shield size={18} className="text-teal-600" /> Approval & Verification Insights
      </h3>

      {/* Section Toggle */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'commercial', label: 'Commercial L1', count: cl.pending_count },
          { key: 'leadinfo', label: 'Lead Verification', count: li.pending_count },
          { key: 'checklist', label: 'Checklist SLA', count: sla.critical_count + sla.urgent_count },
        ].map(s => (
          <button key={s.key} onClick={() => setActiveSection(s.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${activeSection === s.key ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'}`}
            data-testid={`gtm-section-${s.key}`}>
            {s.label} {s.count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{s.count}</span>}
          </button>
        ))}
      </div>

      {activeSection === 'commercial' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Pending Approval" count={cl.pending_count} icon={Clock} color="amber"
              onClick={() => navigate('/leads?approval_status=pending')} />
            <SummaryCard label="Approved (L1)" count={cl.approved_count} icon={CheckCircle} color="emerald"
              onClick={() => navigate('/leads?approval_status=l1_approved')} />
          </div>
          <LeadTable leads={cl.pending} title="Pending L1 Approval" onView={(id) => navigate(`/leads/${id}`)} onApprove={handleApprove} showApprove showEdit />
          <LeadTable leads={cl.approved} title="Approved Leads" onView={(id) => navigate(`/leads/${id}`)} onApprove={() => {}} showApprove={false} showEdit={false} defaultOpen={false} />
        </div>
      )}

      {activeSection === 'leadinfo' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Pending Verification" count={li.pending_count} icon={AlertTriangle} color="amber" />
            <SummaryCard label="Verified" count={li.verified_count} icon={Lock} color="emerald" />
          </div>
          <LeadTable leads={li.pending} title="Pending Lead Info Verification" onView={(id) => navigate(`/leads/${id}`)} onApprove={() => {}} showApprove={false} showEdit />
          <LeadTable leads={li.verified} title="Verified Leads" onView={(id) => navigate(`/leads/${id}`)} onApprove={() => {}} showApprove={false} showEdit={false} defaultOpen={false} />
        </div>
      )}

      {activeSection === 'checklist' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Expected" count={sla.expected_count} icon={CheckCircle} color="emerald"
              onClick={() => navigate('/leads?checklist_status=expected')} />
            <SummaryCard label="Urgent" count={sla.urgent_count} icon={AlertTriangle} color="amber"
              onClick={() => navigate('/leads?checklist_status=urgent')} />
            <SummaryCard label="Critical" count={sla.critical_count} icon={AlertOctagon} color="red"
              onClick={() => navigate('/leads?checklist_status=critical')} />
            <SummaryCard label="Completed" count={sla.completed_count} icon={CheckCircle} color="teal"
              onClick={() => navigate('/leads?checklist_status=completed')} />
          </div>
          <LeadTable leads={sla.critical} title="Critical Checklists" onView={(id) => navigate(`/leads/${id}`)} onApprove={() => {}} showApprove={false} showEdit />
          <LeadTable leads={sla.urgent} title="Urgent Checklists" onView={(id) => navigate(`/leads/${id}`)} onApprove={() => {}} showApprove={false} showEdit defaultOpen={false} />
        </div>
      )}
    </div>
  );
};

// BFR Insights
export const BFRApprovalInsights = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/approval-analytics`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setData(d.bfr);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (leadId) => {
    try {
      await fetch(`${API_BASE}/api/leads/${leadId}/commercial/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-teal-500" /></div>;
  if (!data) return null;

  const cl = data.commercial_l2;

  return (
    <div className="space-y-5" data-testid="bfr-approval-insights">
      <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
        <Shield size={18} className="text-blue-600" /> Commercial Verification (Level 2)
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Pending Approval" count={cl.pending_count} icon={Clock} color="amber"
          onClick={() => navigate('/leads?approval_status=l1_approved')} />
        <SummaryCard label="Approved (L2)" count={cl.approved_count} icon={CheckCircle} color="emerald"
          onClick={() => navigate('/leads?approval_status=l2_approved')} />
      </div>
      <LeadTable leads={cl.pending} title="Pending L2 Approval" onView={(id) => navigate(`/leads/${id}`)} onApprove={handleApprove} showApprove showEdit />
      <LeadTable leads={cl.approved} title="Recently Approved" onView={(id) => navigate(`/leads/${id}`)} onApprove={() => {}} showApprove={false} showEdit={false} defaultOpen={false} />
    </div>
  );
};

// CX Leader Insights
export const CXLeaderApprovalInsights = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/approval-analytics`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setData(d.cx_leader);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (leadId) => {
    try {
      await fetch(`${API_BASE}/api/leads/${leadId}/commercial/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-teal-500" /></div>;
  if (!data) return null;

  const mv = data.mutual_visibility;
  const pf = data.pending_final;

  return (
    <div className="space-y-5" data-testid="cx-leader-approval-insights">
      <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
        <Shield size={18} className="text-indigo-600" /> Approval Summary
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Executive Approvals" count={mv.executive_approvals} icon={Users} color="indigo" />
        <SummaryCard label="CX Leader Approvals" count={mv.cx_leader_approvals} icon={Users} color="teal" />
        <SummaryCard label="Pending Final" count={pf.count} icon={Clock} color="amber"
          onClick={() => navigate('/leads?stage=executive_pending')} />
      </div>
      <LeadTable leads={pf.leads} title="Pending Final Approvals" onView={(id) => navigate(`/leads/${id}`)} onApprove={handleApprove} showApprove showEdit={false} />
    </div>
  );
};

// Executive Insights (Full Dashboard)
export const ExecutiveApprovalInsights = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/approval-analytics`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setData(d.executive);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (leadId) => {
    try {
      await fetch(`${API_BASE}/api/leads/${leadId}/commercial/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-teal-500" /></div>;
  if (!data) return null;

  const fq = data.final_queue;
  const vr = data.verified;
  const mv = data.mutual_visibility;

  return (
    <div className="space-y-5" data-testid="executive-approval-insights">
      <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
        <Shield size={18} className="text-purple-600" /> Executive Dashboard
      </h3>

      {/* Mutual Visibility Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Final Approval Queue" count={fq.pending_count} icon={Clock} color="amber" />
        <SummaryCard label="Verified Leads" count={vr.count} icon={CheckCircle} color="emerald" />
        <SummaryCard label="Executive Approvals" count={mv.executive_approvals} icon={Users} color="indigo" />
        <SummaryCard label="CX Leader Approvals" count={mv.cx_leader_approvals} icon={Users} color="teal" />
      </div>

      {/* Final Approval Queue */}
      <LeadTable leads={fq.pending} title="Final Approval Queue" onView={(id) => navigate(`/leads/${id}`)} onApprove={handleApprove} showApprove showEdit={false} />

      {/* Recently Verified */}
      <LeadTable leads={vr.leads} title="Recently Verified Leads" onView={(id) => navigate(`/leads/${id}`)} onApprove={() => {}} showApprove={false} showEdit={false} defaultOpen={false} />
    </div>
  );
};
